import { useState } from "react"
// The Budget screen's dialog, deliberately: MPN-2040 asks for an "Edit and Save pattern,
// consistent with the Budget screen", and these are the styles that pattern is made of.
// Imported here because the Campaign page never loads the budget module.
import "../budget/segments/segments.scss"
import { formatCurrency } from "src/shared/utils/formatCurrency"
import type {
  CampaignDatesPreview,
  DateEntryEffect,
} from "@services/launches/commands/cmdCampaignDates"

/**
 * What changes on save (MPN-2040).
 *
 * The server computed this with the same code the save runs, so every line here is what
 * will happen rather than what probably will. The only input is which ad groups keep
 * following the campaign; everything else is a consequence of the dates and is listed so it
 * is not discovered afterwards.
 */
export function CampaignDatesConfirm({
  preview,
  busy,
  onCancel,
  onConfirm,
}: {
  preview: CampaignDatesPreview
  busy?: boolean
  onCancel: () => void
  onConfirm: (followAdGroupIds: number[], note: string) => void
}) {
  const following = preview.ad_groups.filter((e) => e.action === "follow")
  // Pre-checked: an ad group that matched the campaign was following it, and 112 of the 127
  // scheduled ad groups in production are in that state. Unticking is the exception.
  const [checked, setChecked] = useState<number[]>(
    following.map((e) => e.ad_group_id).filter((id): id is number => id != null),
  )

  /* Optional, and offered rather than demanded (MPN-1807). The diff already records what
     moved; this is the only place the reason can live, and a required field would just
     collect "update". */
  const [note, setNote] = useState("")

  const trimmed = [...preview.ad_groups, ...preview.ad_flights].filter(
    (e) => e.action === "trim",
  )
  const dropped = [...preview.ad_groups, ...preview.ad_flights].filter(
    (e) => e.action === "unschedule",
  )

  return (
    <div className="seg-dialog" onClick={onCancel}>
      <div
        className="seg-dialog__panel seg-dialog__panel--wide seg-dialog__panel--tall"
        role="dialog"
        aria-modal="true"
        aria-label="What changes on save"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="seg-dialog__title">What changes on save</div>

        <div className="seg-dialog__body">
          Campaign dates move from{" "}
          <strong>
            {fmt(preview.old_start)} – {fmt(preview.old_end)}
          </strong>{" "}
          to{" "}
          <strong>
            {fmt(preview.start)} – {fmt(preview.end)}
          </strong>
          .
        </div>

        {following.length > 0 && (
          <>
            <div className="seg-dialog__group">Ad groups following these dates</div>
            {following.map((entry) => (
              <label className="seg-dialog__row" key={entry.entry_id}>
                <input
                  type="checkbox"
                  checked={entry.ad_group_id != null && checked.includes(entry.ad_group_id)}
                  disabled={entry.ad_group_id == null}
                  onChange={(e) =>
                    entry.ad_group_id != null &&
                    setChecked((was) =>
                      e.target.checked
                        ? was.concat([entry.ad_group_id as number])
                        : was.filter((id) => id !== entry.ad_group_id),
                    )
                  }
                />
                <span className="seg-dialog__row-label">{entry.name}</span>
                <span className="seg-dialog__row-move">
                  {fmt(entry.new_start)} – {fmt(entry.new_end)}
                </span>
              </label>
            ))}
            <p className="seg-dialog__note">
              Unticking one leaves its dates where they are. It is still trimmed if the new
              campaign dates no longer contain it.
            </p>
          </>
        )}

        {trimmed.length > 0 && <EntryList label="Trimmed to the new dates" items={trimmed} />}
        {dropped.length > 0 && (
          <EntryList label="No longer scheduled — outside the new dates" items={dropped} />
        )}

        {preview.windows.length > 0 && (
          <>
            <div className="seg-dialog__group">Spend windows</div>
            {preview.windows.map((w) => (
              <div className="seg-dialog__row" key={w.segment_id}>
                <span className="seg-dialog__row-label">{w.name || "Window"}</span>
                <span className="seg-dialog__row-move">
                  {w.action === "unschedule" ? (
                    <>removed, {formatCurrency(w.amount)} to unallocated</>
                  ) : (
                    <>
                      {formatCurrency(w.amount)} <span className="seg-dialog__arrow">→</span>{" "}
                      {formatCurrency(w.new_amount)}
                    </>
                  )}
                </span>
              </div>
            ))}
            {/* Said rather than implied: the money does not vanish, and where it goes is the
                first thing anyone will ask. */}
            <p className="seg-dialog__note">
              A trimmed window keeps the share of its budget matching the days it keeps. The
              rest returns to Campaign unallocated rather than raising the daily amount over
              the days that are left.
            </p>
          </>
        )}

        {preview.key_dates.length > 0 && (
          <>
            <div className="seg-dialog__group">Key dates now outside the campaign</div>
            {preview.key_dates.map((k) => (
              <div className="seg-dialog__row" key={k.key_date_id}>
                <span className="seg-dialog__row-label">{k.label || "Key date"}</span>
                <span className="seg-dialog__row-move">{fmt(k.date)}</span>
              </div>
            ))}
            <p className="seg-dialog__note">
              Flagged only. Key dates belong to the objective and are shared with its other
              campaigns, so this change does not move them.
            </p>
          </>
        )}

        {preview.contract_ends && (
          <p className="seg-dialog__warn">
            These dates run past the contract, which ends {fmt(preview.contract_ends)}. Allowed
            — contracts get renewed — but worth checking before you save.
          </p>
        )}

        <div className="seg-dialog__group">Note (optional)</div>
        <textarea
          className="seg-dialog__note-input"
          rows={2}
          value={note}
          placeholder="Why are these dates changing?"
          onChange={(e) => setNote(e.target.value)}
        />

        <div className="seg-dialog__actions">
          <button type="button" className="seg-btn" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="seg-btn seg-btn--primary"
            onClick={() => onConfirm(checked, note.trim())}
            disabled={busy}
          >
            {busy ? "Saving…" : "Save dates"}
          </button>
        </div>
      </div>
    </div>
  )
}

function EntryList({ label, items }: { label: string; items: DateEntryEffect[] }) {
  return (
    <>
      <div className="seg-dialog__group">{label}</div>
      {items.map((entry) => (
        <div className="seg-dialog__row" key={entry.entry_id}>
          <span className="seg-dialog__row-label">
            {entry.name}
            <span className="seg-dialog__kind"> · {entry.kind}</span>
          </span>
          <span className="seg-dialog__row-move">
            {entry.action === "unschedule" ? (
              "not scheduled"
            ) : (
              <>
                {fmt(entry.new_start)} – {fmt(entry.new_end)}
              </>
            )}
          </span>
        </div>
      ))}
    </>
  )
}

function fmt(day?: string | null): string {
  if (!day) return "—"
  return new Date(`${day}T00:00:00`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  })
}
